import { ApexOptions } from "apexcharts"
import { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { totalOnHoverTooltip } from "../../../chartTooltip"
import { monthDay, monthDayLabels } from "../../../chartDateAxis"

/*
    Conversions By Day, one line per Journey Step (MPN-1530 §3.2, restyled by MPN-1548).

    A line per step, not a stacked bar. The stack answered "how many conversions today"
    and made the shape of any one step almost unreadable: a step's height depended on
    what was underneath it, so a flat series looked wavy and a step near the bottom was
    a sliver. Ops reads this chart to compare steps against each other over time, which
    is what separate lines are for. The y-axis now means one step's conversions rather
    than the day's combined total.

    Three behaviours the ticket asks for:

    Click to isolate. Clicking a step in the legend redraws the chart as ONE series in a
    single colour, scaled to that series' own max. This is not Apex's built-in legend
    toggle, which hides a series while the rest keep the shared scale -- the point of
    isolating "Register for Classes" is to see its shape, and against an axis set by
    Awareness it stays a flat line at the bottom. Separate lines help but do not fix
    that: they share one y-axis, so a step two orders of magnitude smaller is still
    pinned to the floor. A "Show all" control appears once isolated, and clicking the
    same step again restores every line.

    Key Date overlay. Dashed vertical lines for campaign key dates, drawn via Apex's
    xaxis annotations. Dates outside the chart's visible range are omitted rather than
    clipped to the edge, because a line pinned to the first bar reads as "this happened
    on day one", which is worse than not drawing it.

    Hover. The full view reports the day's total plus a per-step breakdown; Apex's shared
    tooltip gives the breakdown and its own total row is disabled in favour of one we
    control the wording of. The total is worth keeping now that the lines no longer stack
    to it -- it is the one number the chart stopped showing on its own.
*/

const COLORS = ["#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6"]
const ISOLATED_COLOR = "#4338ca"

type StepSeries = {
  name: string
  data: { day_start: string; conversions: number }[]
}

type KeyDate = { date: string; label: string }

export default function ConversionsByDayByStep({ data }: { data: any }) {
  const [isolated, setIsolated] = useState<string | null>(null)

  const stepSeries: StepSeries[] = data?.["conversions-by-day-by-step"] ?? []
  const keyDates: KeyDate[] = data?.["key-dates"] ?? []

  const { series, options, days } = useMemo(() => {
    const days: string[] = stepSeries[0]?.data.map((p) => p.day_start) ?? []

    const shown = isolated
      ? stepSeries.filter((s) => s.name === isolated)
      : stepSeries

    const series = shown.map((s) => ({
      name: s.name,
      data: s.data.map((p) => p.conversions),
    }))

    /*
        Only the key dates inside the plotted window. `days` is the axis, so anything
        outside it has no x position to sit at; Apex would clamp it to the nearest edge.
    */
    const first = days[0]
    const last = days[days.length - 1]
    const visible = keyDates.filter((k) => first && last && k.date >= first && k.date <= last)

    const options: ApexOptions = {
      chart: {
        type: "line",
        height: 380,
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      // One flat colour when isolated: the palette encodes "which step", and a
      // single-series chart has no which to encode.
      colors: isolated ? [ISOLATED_COLOR] : COLORS,
      dataLabels: { enabled: false },
      // Straight segments, not a spline. A smoothed curve invents conversions between
      // two days that did not happen, and on a step that fires in bursts it draws
      // hills either side of a single spike.
      stroke: { width: 2, curve: "straight" },
      // Markers make a day readable when a step is flat, and give the tooltip
      // something to attach to on a series that never moves.
      markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
      grid: { borderColor: "#f1f1f1" },
      xaxis: {
        categories: days,
        tickAmount: 10,
        labels: { ...monthDayLabels, hideOverlappingLabels: true },
      },
      yaxis: {
        // Scaled to whatever is drawn. Isolated, that is the one series' own max, which
        // is the entire reason isolating is useful.
        forceNiceScale: true,
        labels: { formatter: (v: number) => `${Math.round(v)}` },
      },
      // Our own legend below, because isolate is not the same gesture as Apex's
      // built-in show/hide and having both would offer two different meanings for a
      // click on the same word.
      legend: { show: false },
      /*
          §3.2 asks for the day's TOTAL plus the per-step breakdown. Apex's shared
          tooltip lists the steps but never adds them up, which leaves the reader
          summing four numbers to answer "how many conversions on the 14th" -- the
          question the chart is for. Same builder the §3.5 and §4.5 charts use.

          Isolated, there is one series and a total would just repeat it, so the plain
          tooltip is right there.
      */
      tooltip: isolated
        ? {
            intersect: false,
            y: { formatter: (v: number) => `${Math.round(v ?? 0)} conversions` },
          }
        : { shared: true, intersect: false, custom: totalOnHoverTooltip("number", (n) => n) },
      /*
          The x of an annotation is the axis label, not the underlying date (MPN-1772).

          Apex converts a category axis to a numeric one behind our backs -- `xaxis.type`
          comes out "numeric" and `globals.labels` becomes [1, 2, 3...] -- and it resolves a
          string annotation by looking that string up in `globals.categoryLabels`, which
          holds labels AFTER `xaxis.labels.formatter` has run. Ours renders "8/31", so
          "2026-08-31" matched nothing and Apex silently skipped the line: `getX1X2` marks
          an unresolvable x as clipped and `addXaxisAnnotation` draws nothing for it. No
          error, no element, a caption promising dashed lines that were never there.

          So the value here has to be whatever the axis renders, from the same `monthDay`
          the formatter uses -- one function, so a change to the label format cannot move
          the lines off the dates without moving the axis too.

          Passing the category index instead looks like it works and does not: Apex accepts
          the number as a pixel offset and parks every line against the left edge.
      */
      annotations: {
        xaxis: visible.map((k) => ({
          x: monthDay(k.date),
          borderColor: "#94a3b8",
          strokeDashArray: 4,
          label: {
            text: k.label,
            orientation: "horizontal",
            position: "top",
            style: { fontSize: "10px", color: "#475569", background: "#f1f5f9" },
          },
        })),
      },
    }

    return { series, options, days }
  }, [stepSeries, keyDates, isolated])

  if (stepSeries.length === 0) {
    return <p className="text-muted fs-11 mb-0">No conversions in this date range.</p>
  }

  return (
    <div>
      <div className="mon-steplegend">
        {stepSeries.map((step, index) => {
          const on = !isolated || isolated === step.name
          return (
            <button
              key={step.name}
              type="button"
              className={`mon-steplegend__item${on ? "" : " mon-steplegend__item--off"}`}
              // Clicking the isolated step again restores every line, so the control is
              // its own undo and never strands the reader in a single series.
              onClick={() => setIsolated(isolated === step.name ? null : step.name)}
            >
              <span
                className="mon-steplegend__dot"
                style={{ background: isolated === step.name ? ISOLATED_COLOR : COLORS[index % COLORS.length] }}
              />
              {step.name}
            </button>
          )
        })}
        {isolated && (
          <button
            type="button"
            className="mon-steplegend__reset"
            onClick={() => setIsolated(null)}
          >
            Show all ×
          </button>
        )}
      </div>

      <ReactApexChart
        options={options}
        series={series}
        type="line"
        height={380}
      />

      {days.length > 0 && keyDates.length > 0 && (
        <p className="mon-steplegend__note">
          Dashed lines mark campaign key dates falling inside this range.
        </p>
      )}
    </div>
  )
}
